import React, { useState, useEffect } from "react";
import { useClientsReviewsData } from "@/contexts/HomeDataContext";
import { FadeIn } from "@/components/ui/scroll-animated";
import { useTranslations } from "next-intl";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const ClientsReviewsSectionV2: React.FC = () => {
  const data = useClientsReviewsData();
  const t = useTranslations("Reviews");
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  // Get the number of clients from the translation file
  const getClientCount = () => {
    try {
      const clients = t.raw("clients");
      return Array.isArray(clients) ? clients.length : 1;
    } catch (e) {
      return 1;
    }
  };

  const clientCount = getClientCount();

  // Auto-slide functionality with progress animation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReviewIndex((prevIndex) => (prevIndex + 1) % clientCount);
      setProgress(0);
    }, 5000); // Change review every 5 seconds

    return () => clearInterval(interval);
  }, [clientCount]);

  // Progress animation
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress + 100 / (5000 / 50); // 50ms intervals
        return newProgress >= 100 ? 0 : newProgress;
      });
    }, 50);

    return () => clearInterval(progressInterval);
  }, [currentReviewIndex]);

  const currentReview = data.reviews[currentReviewIndex];
  const nextReview = data.reviews[(currentReviewIndex + 1) % clientCount];

  const renderStars = (rating: number | string) => {
    const numRating = typeof rating === "string" ? parseFloat(rating) : rating;
    const stars = [];
    const fullStars = Math.floor(numRating);
    const hasHalfStar = numRating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <svg
          key={i}
          className="w-4 h-4 text-yellow-400"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="relative w-4 h-4">
          {/* Gray background star */}
          <svg
            className="absolute w-4 h-4 text-gray-300"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          {/* Yellow half star - RTL support */}
          <div className="absolute top-0 left-0 rtl:right-0 rtl:left-auto w-1/2 h-full overflow-hidden">
            <svg
              className="w-4 h-4 text-yellow-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        </div>
      );
    }

    const emptyStars = 5 - Math.ceil(numRating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <svg
          key={`empty-${i}`}
          className="w-4 h-4 text-gray-300"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }

    return stars;
  };

  const goToPrevious = () => {
    setCurrentReviewIndex((prevIndex) => (prevIndex - 1 + clientCount) % clientCount);
    setProgress(0);
  };

  const goToNext = () => {
    setCurrentReviewIndex((prevIndex) => (prevIndex + 1) % clientCount);
    setProgress(0);
  };

  return (
    <section className="relative w-full py-16 md:py-20 lg:py-24 bg-[#05092B] overflow-hidden transition-all duration-500 ease-in-out">
      {/* Background SVG */}
      <div className="absolute inset-0 opacity-10">
        <img
          src="/svg/bg_reviewsv2.svg"
          alt="Background pattern"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Header - Centered */}
        <div className="text-center mb-12 md:mb-16">
          <FadeIn delay={200}>
            <div className="text-[#667EE9] font-h1-up font-[number:var(--h1-up-font-weight)] text-[length:var(--h1-up-font-size)] tracking-[var(--h1-up-letter-spacing)] leading-[var(--h1-up-line-height)] [font-style:var(--h1-up-font-style)] uppercase mb-4">
              {t("tagline")}
            </div>
          </FadeIn>

          <FadeIn delay={400}>
            <h2 className="font-heading-h1 font-[number:var(--heading-h1-font-weight)] text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[length:var(--heading-h1-font-size)] tracking-[var(--heading-h1-letter-spacing)] leading-tight [font-style:var(--heading-h1-font-style)] whitespace-pre-line">
              {t("heading")}
            </h2>
          </FadeIn>
        </div>

        {/* Review Cards with Navigation Buttons */}
        <div className="relative flex items-center justify-center mb-8">
          {/* Left Navigation Button - Hidden on mobile, visible on desktop */}
          {clientCount > 1 && (
            <button
              onClick={goToPrevious}
              className="group hidden lg:flex absolute left-0 top-1/2 transform -translate-y-1/2 z-10 items-center justify-center w-12 h-12 bg-brand-gradient rounded-full hover:from-[#2A17B8] hover:to-[#2E5FD1] transition-all duration-300 hover:scale-110 shadow-lg"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          )}

          {/* Right Navigation Button - Hidden on mobile, visible on desktop */}
          {clientCount > 1 && (
            <button
              onClick={goToNext}
              className="group hidden lg:flex absolute right-0 top-1/2 transform -translate-y-1/2 z-10 items-center justify-center w-12 h-12 bg-brand-gradient rounded-full hover:from-[#2A17B8] hover:to-[#2E5FD1] transition-all duration-300 hover:scale-110 shadow-lg"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          )}

          {/* Review Cards Container */}
          <div
            className={
              clientCount === 1
                ? "w-full max-w-3xl mx-auto"
                : "grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 w-full max-w-6xl"
            }
          >
            {/* Current Review Card */}
            <FadeIn delay={600}>
              <div className="bg-[#1A203B] rounded-lg p-4 sm:p-6 md:p-8 shadow-xl relative group cursor-pointer hover:shadow-2xl transition-all duration-500 ease-in-out w-full">
                {/* Quote Mark */}
                <div className="absolute bottom-4 right-4">
                  <img
                    src="/svg/review_quote_marks.svg"
                    alt="Quote marks"
                    className="w-12 h-8 sm:w-14 sm:h-10"
                  />
                </div>

                {/* Client Info */}
                <div className="flex items-center mb-4 sm:mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-600 rounded-full mr-3 sm:mr-4 flex items-center justify-center">
                    <span className="text-white font-bold text-base sm:text-lg">
                      {t(`clients.${currentReviewIndex}.name`).charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-heading-h4 font-[number:var(--heading-h4-font-weight)] text-white text-[length:var(--heading-h4-font-size)] tracking-[var(--heading-h4-letter-spacing)] leading-[var(--heading-h4-line-height)] [font-style:var(--heading-h4-font-style)]">
                      {t(`clients.${currentReviewIndex}.name`)}
                    </h3>
                    <p className="text-gray-400 font-body-m font-[number:var(--body-m-font-weight)] text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)]">
                      {t(`clients.${currentReviewIndex}.title`)}
                    </p>
                  </div>
                </div>

                {/* Review Text with smooth expand on hover */}
                <div className="relative overflow-hidden transition-[max-height] duration-500 ease-in-out max-h-24 group-hover:max-h-96">
                  <p className="text-gray-300 font-body-m font-[number:var(--body-m-font-weight)] text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-relaxed [font-style:var(--body-m-font-style)]">
                    {t(`clients.${currentReviewIndex}.review`)}
                  </p>
                  {/* Optional fade gradient */}
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#1A203B] to-transparent group-hover:opacity-0 transition-opacity duration-300" />
                </div>
              </div>
            </FadeIn>

            {/* Next Review Card - Hidden on mobile, visible on desktop */}
            {clientCount > 1 && (
              <FadeIn delay={800}>
                <div className="hidden lg:block bg-[#1A203B] rounded-lg p-4 sm:p-6 md:p-8 shadow-xl relative group cursor-pointer hover:shadow-2xl transition-all duration-500 ease-in-out w-full">
                  {/* Quote Mark */}
                  <div className="absolute bottom-4 right-4">
                    <img
                      src="/svg/review_quote_marks.svg"
                      alt="Quote marks"
                      className="w-12 h-8 sm:w-14 sm:h-10"
                    />
                  </div>

                  {/* Client Info */}
                  <div className="flex items-center mb-4 sm:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-600 rounded-full mr-3 sm:mr-4 flex items-center justify-center">
                      <span className="text-white font-bold text-base sm:text-lg">
                        {t(
                          `clients.${(currentReviewIndex + 1) % clientCount}.name`
                        ).charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-heading-h4 font-[number:var(--heading-h4-font-weight)] text-white text-[length:var(--heading-h4-font-size)] tracking-[var(--heading-h4-letter-spacing)] leading-[var(--heading-h4-line-height)] [font-style:var(--heading-h4-font-style)]">
                        {t(
                          `clients.${(currentReviewIndex + 1) % clientCount}.name`
                        )}
                      </h3>
                      <p className="text-gray-400 font-body-m font-[number:var(--body-m-font-weight)] text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)]">
                        {t(
                          `clients.${(currentReviewIndex + 1) % clientCount}.title`
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Review Text with smooth expand on hover */}
                  <div className="relative overflow-hidden transition-[max-height] duration-500 ease-in-out max-h-24 group-hover:max-h-96">
                    <p className="text-gray-300 font-body-m font-[number:var(--body-m-font-weight)] text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-relaxed [font-style:var(--body-m-font-style)]">
                      {t(
                        `clients.${(currentReviewIndex + 1) % clientCount}.review`
                      )}
                    </p>
                    <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#1A203B] to-transparent group-hover:opacity-0 transition-opacity duration-300" />
                  </div>
                </div>
              </FadeIn>
            )}
          </div>
        </div>

        {/* Mobile Navigation Buttons - Only visible on mobile */}
        {clientCount > 1 && (
          <div className="flex lg:hidden justify-center items-center gap-4 mb-8">
            <button
              onClick={goToPrevious}
              className="group flex items-center justify-center w-12 h-12 bg-brand-gradient rounded-full hover:from-[#2A17B8] hover:to-[#2E5FD1] transition-all duration-300 hover:scale-110 shadow-lg"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>

            <div className="text-white text-sm font-medium px-4">
              {currentReviewIndex + 1} / {clientCount}
            </div>

            <button
              onClick={goToNext}
              className="group flex items-center justify-center w-12 h-12 bg-brand-gradient rounded-full hover:from-[#2A17B8] hover:to-[#2E5FD1] transition-all duration-300 hover:scale-110 shadow-lg"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </div>
        )}

        {/* Progress Indicator */}
        {clientCount > 1 && (
          <FadeIn delay={1000}>
            <div className="flex justify-center">
              <div className="flex space-x-2 rtl:space-x-reverse">
                {Array.from({ length: clientCount }, (_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentReviewIndex(index);
                      setProgress(0);
                    }}
                    className={`h-1.5 md:h-1 rounded-full transition-all duration-300 ${
                      index === currentReviewIndex ? "w-8" : "w-2"
                    }`}
                  >
                    {index === currentReviewIndex ? (
                      <div className="relative w-full h-full bg-gray-600 rounded-full overflow-hidden">
                        <div
                          className="absolute top-0 left-0 rtl:right-0 rtl:left-auto h-full bg-brand-gradient rounded-full transition-all duration-75 ease-linear"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gray-600 hover:bg-gray-500 rounded-full transition-colors duration-300" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </FadeIn>
        )}
      </div>
    </section>
  );
};
